import React from 'react'
import clsx from 'clsx'
import i18n from '@pureartisan/simple-i18n'
import shallow from 'zustand/shallow'
import { withStyles, alpha, Icon } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { OPTIONS_ACTIVE_POINTS } from '../../../../constants/map'
import useStore from '../store'
import { getLongPopName } from '../utils'
import { PanelBody, PanelHeader } from '../SlideoutPanel'
import { SwitchControl } from '../Controls'
import Tooltip from '../Tooltip'
import DemoChip from '../DemoChip/DemoChip'
import GeoChip from '../GeoChip/GeoChip'
import { theme } from '../theme'
import OverlayDemoIcon from '../../../assets/Icons/OverlayDemoIcon'
import OverlayRedliningIcon from '../../../assets/Icons/OverlayRedliningIcon'
import OverlayZipsIcon from '../../../assets/Icons/OverlayZipsIcon'

// Styles for this component.
const styles = theme => ({
  root: {
    '& .panel-body': {
      '& > *': {
        padding: theme.spacing(2.5, 3),
      },
    },
  },
  controlSection: {
    borderTop: theme.extras.styles.faintBorder,
    paddingBottom: theme.spacing(2),
    position: 'relative',
    '&.main': {
      borderTop: 'none',
    },
  },
  controlContext: {
    display: 'flex',
    paddingTop: theme.spacing(2),
    '& svg, img': {
      height: '44px',
      flex: '44px 0 0',
      paddingRight: theme.spacing(1.5),
    },
    '& p': {
      fontSize: '12px',
    },
  },
  control: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '& .MuiToggleButtonGroup-root': {
      '& .MuiToggleButton-root': {
        // so it can trigger tooltip
        pointerEvents: 'all',
      },
      '& .Mui-selected': {
        backgroundColor:
          theme.extras.variables.colors.accent,
        border: `1px solid ${theme.extras.variables.colors.accentBackground}`,
        '& p': {
          color: theme.extras.variables.colors.ddkMainBlue,
        },
      },
      '& .Mui-disabled p': {
        color: theme.extras.variables.colors.textSubtle,
      },
    },

    '&.overlays': {
      display: 'flex',
      flexDirection: 'column',
      paddingBottom: 0,

      '& .MuiFormControlLabel-root': {
        '&:focus-within': {
          '& .MuiCheckbox-root:not(.Mui-checked)': {
            backgroundColor: '#0000000d',
          },
        },
        margin: 0,
        '& .MuiCheckbox-root': {
          color: theme.extras.variables.colors.pictonBlue,
          padding: theme.spacing(0.25),
          marginLeft: theme.spacing(-0.25),
          marginRight: theme.spacing(0.75),
          '&:not(.Mui-checked), &.Mui-disabled': {
            color: theme.extras.variables.colors.faintGray,
          },
          '& .demo-icon': {
            width: 13,
            paddingRight: theme.spacing(0.75),
          },
        },
      },

      '& .geoChip': {
        marginTop: theme.spacing(0.5),
      },
    },
  },
  controlLabel: {
    ...theme.typography.body2,
    display: 'block',
    position: 'relative',
    width: '100%',
    paddingBottom: theme.spacing(0.5),
    color: theme.extras.variables.colors.textSubtle,
    '&.Mui-focused': {
      color: theme.extras.variables.colors.textSubtle,
    },
  },
  overlaysHeading: {
    display: 'inline-flex',
    '& .MuiSwitch-root': {
      marginLeft: 'auto',
    },
  },
})

const OverlaysPanel = ({
  classes,
  className,
  ...props
}) => {
  const {
    activePointLayers,
    activeRedliningTypes,
    setStoreValues,
    showDensityOverlays,
    toggleDensityOverlays,
    showZipBoundaries,
    redliningActive,
    activeGeoType,
  } = useStore(
    state => ({
      activeYear: state.activeYear,
      activeNorm: state.activeNorm,
      activePointLayers: state.activePointLayers,
      activeRedliningTypes: state.activeRedliningTypes,
      activeRegion: state.activeRegion,
      setStoreValues: state.setStoreValues,
      showDensityOverlays: state.showDensityOverlays,
      toggleDensityOverlays: state.toggleDensityOverlays,
      displayPopup: state.displayPopup,
      showZipBoundaries: state.showZipBoundaries,
      redliningActive: state.redliningActive,
      activeGeoType: state.activeGeoType,
      remoteJson: state.remoteJson,
      flyToBounds: state.flyToBounds,
      flyToFeature: state.flyToFeature,
    }),
    shallow,
  )

  /** Handle changes to the point layer checkboxes */
  const handleActivePointLayers = event => {
    const name = event.currentTarget.name
    let layers =
      activePointLayers.length > 0
        ? activePointLayers.slice()
        : []
    const ind = layers.indexOf(name)
    if (ind >= 0) {
      layers.splice(ind, 1)
    } else {
      layers.push(name)
    }
    setStoreValues({
      activePointLayers: layers,
    })
  }

  /** Handle changes to the redlining checkboxes */
  const handleActiveRedliningTypes = event => {
    const name = event.currentTarget.name
    let layers =
      activeRedliningTypes.length > 0
        ? activeRedliningTypes.slice()
        : []
    const ind = layers.indexOf(name)
    if (ind >= 0) {
      layers.splice(ind, 1)
    } else {
      layers.push(name)
    }
    setStoreValues({
      activeRedliningTypes: layers,
    })
  }

  const toggleShowZipBoundaries = () => {
    setStoreValues({
      showZipBoundaries: !showZipBoundaries,
    })
  }
  const toggleRedliningActive = () => {
    const newVals = {
      redliningActive: !redliningActive,
    }
    // if activating redlining and no types active, activate all
    if (!redliningActive && !activeRedliningTypes.length) {
      newVals.activeRedliningTypes = ['A', 'B', 'C', 'D']
    }
    setStoreValues(newVals)
  }

  const getPointOption = (type, op, i) => {
    const isRedlining = type === 'redlining'
    return (
      <Box display={'flex'}>
        <FormControlLabel
          classes={{
            label: classes[`checkboxColor_${op}`],
          }}
          control={
            <Checkbox
              color="secondary"
              checked={
                isRedlining
                  ? activeRedliningTypes.indexOf(op) > -1
                  : activePointLayers.indexOf(op) > -1
              }
              onChange={
                isRedlining
                  ? handleActiveRedliningTypes
                  : handleActivePointLayers
              }
              name={op}
            />
          }
          label={
            isRedlining ? (
              <GeoChip
                id={op}
                text={'Grade ' + op}
                border={theme.extras.redlining[op]}
                fill={alpha(
                  theme.extras.redlining[op],
                  0.15,
                )}
              />
            ) : (
              <DemoChip
                demo={op}
                color={theme.extras.redlining[op]}
                nameFormatter={getLongPopName}
              />
            )
          }
          key={type + op}
        />
      </Box>
    )
  }

  const redliningTypes = Object.keys(theme.extras.redlining)

  return (
    <FormControl
      className={clsx(
        classes.root,
        className,
        'panel-root',
      )}
    >
      <PanelHeader
        title={i18n.translate('CONTROL_PANEL_OVERLAYS')}
      />
      <PanelBody>
        <Box
          className={clsx(
            classes.controlSection,
            'control-section',
          )}
        >
          <Box>
            <SwitchControl
              checked={showDensityOverlays}
              onChange={toggleDensityOverlays}
            />
            <Typography
              variant="h2"
              className={classes.overlaysHeading}
            >
              {i18n.translate(`LEGEND_DEMO`)}
              <Tooltip
                title={i18n.translate(`DEMO_OVERLAY_TIP`)}
              />
            </Typography>
          </Box>
          <Box className={classes.controlContext}>
            <OverlayDemoIcon />
            <Typography variant="body1">
              {i18n.translate(`DEMO_OVERLAY_CONTEXT`)}
            </Typography>
          </Box>
          {showDensityOverlays && (
            <Box
              className={clsx(classes.control, 'overlays')}
            >
              {OPTIONS_ACTIVE_POINTS.options
                .slice()
                .reverse()
                .map(getPointOption.bind(null, 'demos'))}
            </Box>
          )}
        </Box>
        {activeGeoType === 'tracts' && (
          <Box
            className={clsx(
              classes.controlSection,
              'control-section',
            )}
          >
            <Box className="user-hints-target-exploreRedlining">
              <SwitchControl
                checked={redliningActive}
                onChange={toggleRedliningActive}
              />
              <Typography
                variant="h2"
                className={classes.overlaysHeading}
              >
                {i18n.translate(`LEGEND_REDLINING`)}
                <Tooltip
                  title={i18n.translate(
                    `REDLINING_OVERLAY_TIP`,
                  )}
                />
              </Typography>
            </Box>
            <Box className={classes.controlContext}>
              <OverlayRedliningIcon />
              <Typography variant="body1">
                {i18n.translate(
                  `REDLINING_OVERLAY_CONTEXT`,
                )}
              </Typography>
            </Box>
            {redliningActive && (
              <>
                <Box
                  className={clsx(
                    classes.control,
                    'overlays',
                  )}
                >
                  {redliningTypes.map(
                    getPointOption.bind(null, 'redlining'),
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
        <Box
          className={clsx(
            classes.controlSection,
            'control-section',
          )}
        >
          <Box>
            <SwitchControl
              checked={showZipBoundaries}
              onChange={toggleShowZipBoundaries}
            />
            <Typography
              variant="h2"
              className={classes.overlaysHeading}
            >
              {i18n.translate(`LEGEND_ZIP_OVERLAY`)}
              <Tooltip
                title={i18n.translate(`ZIP_OVERLAY_TIP`)}
              />
            </Typography>
          </Box>
          <Box className={classes.controlContext}>
            <OverlayZipsIcon />
            <Typography variant="body1">
              {i18n.translate(`ZIP_OVERLAY_CONTEXT`)}
            </Typography>
          </Box>
        </Box>
      </PanelBody>
    </FormControl>
  )
}

export default withStyles(styles)(OverlaysPanel)
